import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { gastoPorCategoria } from '#components/mobile/informes/calculos';
import {
  formatPorcentaje,
  TarjetaInforme,
} from '#components/mobile/informes/comunes';
import { usePresupuestado } from '#components/mobile/informes/usePresupuestado';
import { useTotalesMensuales } from '#components/mobile/informes/useTotalesMensuales';
import { color } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

import { resumenPlan } from './diario';
import { nombrePeriodo } from './textos';
import { useResumenDiario } from './useResumenDiario';

/** Tarjeta «Tu semana» de Informes: la última semana cerrada. */
export function TarjetaDiarioSemana({
  enRejilla = false,
}: {
  enRejilla?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const { resumen: r, periodo } = useResumenDiario('semana');
  let frase = nombrePeriodo(t, periodo, i18n.language);
  let tono: 'neutro' | 'bien' | 'aviso' = 'neutro';
  if (r?.gastoHabitual != null) {
    const dif = r.gastos - r.gastoHabitual;
    if (Math.abs(dif) >= 1000) {
      frase = `${frase} · ${
        dif < 0
          ? t('{{amount}} less than a normal week', {
              amount: format(-dif, 'financial'),
            })
          : t('{{amount}} more than a normal week', {
              amount: format(dif, 'financial'),
            })
      }`;
      tono = dif < 0 ? 'bien' : 'aviso';
    }
  }
  const maximo = Math.max(...(r?.porDia ?? [0]), 1);
  return (
    <TarjetaInforme
      enRejilla={enRejilla}
      data-testid="tarjeta-diario-semana"
      titulo={t('Your week')}
      valor={r ? format(r.gastos, 'financial') : '…'}
      frase={r ? frase : ''}
      tono={tono === 'aviso' ? 'neutro' : tono}
      onPress={() => void navigate('/reports/diario/semana')}
    >
      {r && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: 4,
            height: 34,
          }}
          aria-hidden
        >
          {r.porDia.map((v, i) => (
            <View
              key={i}
              style={{
                flex: 1,
                height: v > 0 ? Math.max((v / maximo) * 34, 3) : 2,
                borderRadius: 3,
                backgroundColor: v > 0 ? color.accent : color.surface2,
              }}
            />
          ))}
        </View>
      )}
    </TarjetaInforme>
  );
}

/** Tarjeta «Tu mes» de Informes: el último mes (o periodo) cerrado. */
export function TarjetaDiarioMes({
  enRejilla = false,
}: {
  enRejilla?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const format = useFormat();
  const navigate = useNavigate();
  const { resumen: r, mes } = useResumenDiario('mes');
  const { movimientos, categorias } = useTotalesMensuales({
    meses: 1,
    hasta: mes,
  });
  const { presupuestado } = usePresupuestado([mes]);
  const plan = resumenPlan(
    gastoPorCategoria(movimientos, [mes], categorias).filas,
    presupuestado.get(mes) ?? new Map(),
    categorias,
  );
  const partes: string[] = [];
  if (r && r.tasa != null && r.ahorro >= 0) {
    partes.push(
      t('You kept {{pct}} of what came in.', {
        pct: formatPorcentaje(r.tasa, false),
      }),
    );
  } else if (r && r.ahorro < 0) {
    partes.push(t('More went out than came in.'));
  }
  if (plan.total > 0 && plan.asignado > 0) {
    partes.push(
      t('{{inside}} of {{total}} categories within the plan.', {
        inside: plan.dentro,
        total: plan.total,
      }),
    );
  }
  return (
    <TarjetaInforme
      enRejilla={enRejilla}
      data-testid="tarjeta-diario-mes"
      titulo={t('Your month · {{month}}', {
        month: monthUtils.format(mes, 'MMMM', locale),
      })}
      valor={r ? format(r.ahorro, 'financial-with-sign') : '…'}
      frase={partes.join(' ')}
      tono={r ? (r.ahorro >= 0 ? 'bien' : 'mal') : 'neutro'}
      onPress={() => void navigate('/reports/diario/mes')}
    />
  );
}
